import { sumBy } from "lodash";

import { EU_INCLUDE_FEES_COUNTRIES } from "@app/constants/countries";
import { EU_INCLUDE_FEES_SOURCES } from "@app/constants/sources";
import { MoneyCeiled } from "@app/lib/money";
import type { PriceMode } from "@app/modules/results/constants/price-mode";
import { PRICE_MODE } from "@app/modules/results/constants/price-mode";
import type { Fx } from "@app/services/fx";
import type {
  PriceCalendarDeparture,
  PriceCalendarDepartureWithFeesIncluded,
  PriceCalendarPrice
} from "@app/services/price-calendar-service-api";
import type { CurrencyCode } from "@app/types/currency";
import type { Features } from "@app/types/experiments";

import type { SupportedLocale } from "@config/locales";

import { CALIFORNIA_STATE_CODE, MINNESOTA_STATE_CODE } from "./region";

const EXCLUDE_ALL_FEES = 0;
const INCLUDE_SUPPLIER_FEES_ONLY = 1;
const INCLUDE_ALL_FEES = 2;

type FEE_INCLUSION_LEVEL = 0 | 1 | 2;

export const IncludedFees = {
  NONE: "NONE",
  SUPPLIER_ONLY: "SUPPLIER_ONLY",
  ALL: "ALL"
} as const;

export type IncludedFees = (typeof IncludedFees)[keyof typeof IncludedFees];

export interface DisplayedPriceConfig {
  features: Partial<Features>;
  locale: SupportedLocale | undefined | null;
  origin_country: string | undefined; // PoSU
  price_mode?: PriceMode;
  should_include_fees_via_experiments?: boolean;
  sources?: string[];
  user_country: string | undefined;
  user_region?: string | undefined;
}

export function shouldIncludeFees(options: DisplayedPriceConfig) {
  const fee_inclusion_level = Math.max(
    applyCanadaFeeRules(options),
    applyGBFeeRules(options),
    applySemPortugalFeeRules(options),
    applyEuropeanUnionFeeRules(options),
    applyCaliforniaFeeRules(options),
    applyMinnesotaFeeRules(options),
    applyPriceMode(options),
    applyFeesExperiements(options)
  ) as FEE_INCLUSION_LEVEL;
  return [IncludedFees.NONE, IncludedFees.SUPPLIER_ONLY, IncludedFees.ALL][
    fee_inclusion_level
  ];
}

/**
 * BILL C-59
 *
 * Only additional fees that a seller can “drip” (i.e., not disclose upfront)
 * are those imposed directly on a purchaser by provincial or federal legislation (e.g., sales taxes).
 *
 * https://www.canadianadvertisinglaw.com/drip-pricing/
 *
 * @param root0
 * @param root0.user_country_code
 * @param root0.user_country
 */
function applyCanadaFeeRules({
  user_country
}: DisplayedPriceConfig): FEE_INCLUSION_LEVEL {
  if (user_country === "CA") {
    return INCLUDE_ALL_FEES;
  } else {
    return EXCLUDE_ALL_FEES;
  }
}

/**
 * Pricing rules for travel tickets
 *
 * EU rules on pricing also apply when you buy travel tickets, such as flights or train tickets, either online or in person.
 * This means that when you buy your tickets, all taxes, fees and charges must be included and appear in the total price from the beginning of the booking process.
 * This makes it easier for you to compare prices with other travel operators.
 *
 * Any optional supplements (such as travel insurance) must be clearly indicated as such and suggested only on an opt-in basis.
 *
 * https://europa.eu/youreurope/citizens/consumers/shopping/pricing-payments/index_en.htm#inline-nav-6
 *
 * @param root0
 * @param root0.locale
 * @param root0.sources
 */
function applyEuropeanUnionFeeRules({
  locale,
  sources
}: DisplayedPriceConfig): FEE_INCLUSION_LEVEL {
  const LOCALES = ["it", "de", "fr"];
  const SOURCES = ["flixbuseur"];

  if (!locale || !LOCALES.includes(locale)) {
    return EXCLUDE_ALL_FEES;
  }
  if (!sources?.find(source => SOURCES.includes(source))) {
    return EXCLUDE_ALL_FEES;
  }

  return INCLUDE_SUPPLIER_FEES_ONLY;
}
/**
 * SEM test
 *
 * include all fees in the price for PT only for utm_medium=sem
 *
 * @param posu
 * @param utm_medium
 * @param posu.features
 * @param posu.origin_country
 */
function applySemPortugalFeeRules({
  features,
  origin_country
}: DisplayedPriceConfig): FEE_INCLUSION_LEVEL {
  if (features.INCLUDE_FEES_SEM_PT && origin_country === "PT") {
    return INCLUDE_ALL_FEES;
  }

  return EXCLUDE_ALL_FEES;
}

/**
 * Digital Markets, Competition and Consumers Bill 2024 (implementation April 2025)
 *
 * All mandatory fees, such as admin fees or ticket booking fees, must now be included in the headline price and can’t
 * be deceptively dripped in throughout the checkout process, to dupe customers into paying more than they originally
 * bargained for.
 *
 * https://www.gov.uk/government/news/fake-reviews-and-sneaky-hidden-fees-banned-once-and-for-all
 *
 * @param user_country.user_country
 * @param user_country
 */
function applyGBFeeRules({
  user_country
}: DisplayedPriceConfig): FEE_INCLUSION_LEVEL {
  if (user_country === "GB") {
    return INCLUDE_ALL_FEES;
  }

  return EXCLUDE_ALL_FEES;
}

/**
 * California SB 478
 *
 * California Senate Bill 478 bans hidden fees charged for goods and services.
 *
 * https://www.hklaw.com/en/insights/publications/2024/06/california-law-bans-hidden-fees-for-goods-and-services-starting-july
 *
 * @param user_region.user_region
 * @param user_region
 */
function applyCaliforniaFeeRules({
  user_region
}: DisplayedPriceConfig): FEE_INCLUSION_LEVEL {
  if (user_region === CALIFORNIA_STATE_CODE) {
    return INCLUDE_ALL_FEES;
  } else {
    return EXCLUDE_ALL_FEES;
  }
}

/**
 * Law requires advertised prices to include all mandatory fees or surcharges
 *
 * https://www.house.mn.gov/NewLaws/story/2024/5582
 *
 * @param user_region.user_region
 * @param user_region
 */
function applyMinnesotaFeeRules({
  user_region
}: DisplayedPriceConfig): FEE_INCLUSION_LEVEL {
  if (user_region === MINNESOTA_STATE_CODE) {
    return INCLUDE_ALL_FEES;
  } else {
    return EXCLUDE_ALL_FEES;
  }
}

function applyPriceMode({ price_mode }: DisplayedPriceConfig) {
  if (
    price_mode &&
    [PRICE_MODE.FEES, PRICE_MODE.FEES_AND_TAXES].includes(price_mode)
  ) {
    return INCLUDE_ALL_FEES;
  } else {
    return EXCLUDE_ALL_FEES;
  }
}

function applyFeesExperiements({
  should_include_fees_via_experiments
}: DisplayedPriceConfig) {
  if (should_include_fees_via_experiments) {
    return INCLUDE_ALL_FEES;
  } else {
    return EXCLUDE_ALL_FEES;
  }
}

/**
 * Gets the calculation function to get the price with fees or without fees.
 *
 * @param options
 * @returns
 */
function getPriceCalculationFunction(options: DisplayedPriceConfig) {
  if (shouldIncludeFees(options)) {
    return (price: PriceCalendarPrice) =>
      price.breakdown.base + price.breakdown.fees;
  }

  return (price: PriceCalendarPrice) => price.breakdown.base;
}

/**
 * This function consolidates the price of the *price calendar* for each departure.
 *
 * @param departures
 * @param options
 */
export function consolidateCalendarPrices(
  departures: PriceCalendarDeparture[],
  options: DisplayedPriceConfig
): PriceCalendarDepartureWithFeesIncluded[] {
  const priceCalculationFunction = getPriceCalculationFunction(options);

  return departures?.map(departure => ({
    ...departure,
    // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    currency: departure.prices[0].currency,
    fees_included_price: sumBy(departure.prices, price => {
      return priceCalculationFunction(price);
    })
  }));
}

export function shouldIncludeTaxes(
  user_country_code: string | undefined,
  sources: string[],
  price_mode: PriceMode
) {
  return !!(
    (user_country_code &&
      EU_INCLUDE_FEES_COUNTRIES.includes(user_country_code) &&
      sources.find(source => EU_INCLUDE_FEES_SOURCES.includes(source))) ||
    price_mode === PRICE_MODE.FEES_AND_TAXES
  );
}

export interface Price {
  currency: CurrencyCode;
  cents: number;
}

export function convertToCurrency(
  fx: Fx,
  price: Price,
  currency: CurrencyCode
): Price {
  return {
    cents: fx.convert(price.cents, {
      from: price.currency,
      to: currency
    }),
    currency
  };
}

export function formatPriceWithoutCents(
  price: Price,
  locale: SupportedLocale
): string {
  return new MoneyCeiled(price.cents).getPriceWithoutCents(
    price.currency,
    locale
  );
}
